import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPassword, confirmForgotPassword } from '../utils/cognito';
import FocusGuard from './FocusGuard';
import PasswordToggle from './PasswordToggle';
import './Auth.css';

/**
 * Password reset via Cognito: request a code by email (step "request"), then
 * enter the code + a new password (step "reset"). Net-new functionality the app
 * didn't have before Cognito.
 */
const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setIsLoading(true);
    try {
      await forgotPassword(email);
      setInfo('If that email has an account, a reset code is on its way.');
      setStep('reset');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start password reset');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await confirmForgotPassword(email, code.trim(), newPassword);
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main-content" className="auth-container">
      <FocusGuard landmarkId="forgot-form" />
      <div className="auth-card">
        <h2>Reset your password</h2>

        {step === 'request' ? (
          <form onSubmit={handleRequest} className="auth-form" id="forgot-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            {error && <div className="error-message" role="alert" aria-live="assertive">{error}</div>}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? 'Sending…' : 'Send reset code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="auth-form" id="forgot-form">
            {info && <div className="info-message" role="status">{info}</div>}
            <div className="form-group">
              <label htmlFor="code">Verification code</label>
              <input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-password">New password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={isLoading}
                />
                <PasswordToggle shown={showPassword} onToggle={() => setShowPassword(p => !p)} />
              </div>
            </div>
            {error && <div className="error-message" role="alert" aria-live="assertive">{error}</div>}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? 'Resetting…' : 'Set new password'}
            </button>
          </form>
        )}

        <div className="auth-links">
          <p><Link to="/login">Back to login</Link></p>
        </div>
      </div>
    </main>
  );
};

export default ForgotPassword;
